/**
 * The centred overlay: one thing to read or fill in, and nothing behind it
 * reachable until it is dealt with.
 *
 * The decision about the native `<dialog>` element, and the jsdom fact that
 * settled it, is written out in full at the top of `overlays/overlay.ts`. This
 * file assembles the primitives from there; it does not re-argue them.
 *
 * Entry animates and exit does not. Animating the exit means keeping the
 * children mounted through the transition, and the content of a modal is
 * routinely about the record the closing action just changed: a confirmation
 * about a project that has now been archived, a form for a case that has now
 * moved. Two hundred milliseconds of a fading, out-of-date panel is worse than
 * an instant dismissal, and it is also two hundred milliseconds in which a
 * stale button is still clickable.
 */

import { useId, useRef } from 'react'
import type { ReactNode, RefObject } from 'react'
import { createPortal } from 'react-dom'
import { CloseIcon } from '@/components/icons/Icons'
import { Button } from '@/components/primitives'
import {
  scrimPointerDown,
  useBackgroundInert,
  useEscapeToClose,
  useFocusTrap,
  useOverlayPortal,
} from '../overlay'
import styles from './Modal.module.css'

export interface ModalProps {
  open: boolean
  onClose(): void
  /** Visible heading, and the dialog's accessible name. */
  title: string
  /** One line saying what this is for. Announced with the dialog on open. */
  description?: string
  children: ReactNode
  /** The action row. Put the primary action last, where the eye finishes. */
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg'
  /**
   * Set false for a modal in the middle of something that cannot be abandoned
   * halfway, such as a running import. Escape and the close button still work,
   * so this never leaves someone trapped: it removes the dismissal that happens
   * by accident, not the two that are deliberate.
   */
  dismissOnScrim?: boolean
  /** Overrides the default of focusing the panel. See `useFocusTrap`. */
  initialFocusRef?: RefObject<HTMLElement | null>
  className?: string
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissOnScrim = true,
  initialFocusRef,
  className,
}: ModalProps) {
  const host = useOverlayPortal(open)
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const descriptionId = useId()

  useEscapeToClose(open, onClose)
  useBackgroundInert(open)
  useFocusTrap(open, panelRef, initialFocusRef)

  if (!host) return null

  const panelClassName = [styles.panel, styles[size], className].filter(Boolean).join(' ')

  return createPortal(
    <div
      className={styles.scrim}
      onPointerDown={dismissOnScrim ? scrimPointerDown(onClose) : undefined}
    >
      <div
        ref={panelRef}
        className={panelClassName}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        /* Receives initial focus so the name and description are announced
           before any control is. Not a tab stop: the trap's selector excludes
           tabindex="-1". */
        tabIndex={-1}
      >
        <header className={styles.header}>
          <div className={styles.headings}>
            <h2 className={styles.title} id={titleId}>
              {title}
            </h2>
            {description ? (
              <p className={styles.description} id={descriptionId}>
                {description}
              </p>
            ) : null}
          </div>
          {/* The name is "Close" in every state, so it can be queried for. */}
          <Button variant="ghost" size="sm" iconOnly aria-label="Close" onClick={onClose}>
            <CloseIcon size={18} />
          </Button>
        </header>

        <div className={styles.body}>{children}</div>

        {footer ? <footer className={styles.footer}>{footer}</footer> : null}
      </div>
    </div>,
    host,
  )
}
